/**
 * RewardEditor - 報酬編集コンポーネント
 *
 * RewardItem（itemType + option + acquiredAmount）の編集UIを提供する汎用コンポーネント。
 * ギフト配布、ガチャ報酬、ミッション報酬など様々な場面で利用可能。
 */
import React, { useEffect, useMemo, useState } from "react";
import { enumDocumentationData } from "../../api/generated/EnumDocs";
import {
  MasterDataClient,
  MasterDataSelectors,
} from "../../types/generated/MasterDataClient";
import type {
  MstCurrency,
  MstCurrencyID,
} from "../../types/generated/MasterTypes";
import {
  MstCurrencySelector,
  SearchColumnConfig,
} from "../../types/generated/widgets/MstCurrencySelector";
import "./RewardEditor.css";

// 通貨セレクターの検索対象カラム設定
const CURRENCY_SEARCH_COLUMNS: SearchColumnConfig<MstCurrency>[] = [
  { key: "id", type: "number", displayName: "id" },
  { key: "label", type: "string", displayName: "label" },
  { key: "name", type: "string", displayName: "name" },
  { key: "currency_type", type: "string", displayName: "currency_type" },
];

// ========================================
// RewardItem型の定義（バックエンドと同期）
// ========================================
export interface RewardItem {
  itemType: string | number; // アイテム種別（文字列または数値: "free_dia" または 1 など）
  option: string; // オプション（マスタIDなど）
  acquiredAmount: number; // 獲得数量
}

// ========================================
// Protobuf enum値から文字列へのマッピング
// ========================================
const ITEM_TYPE_ENUM_TO_STRING: Record<number, string> = {
  0: "none",
  1: "free_dia_currency",
  2: "paid_dia_currency",
  3: "icon",
  4: "icon_frame",
  5: "shogi_board",
  6: "shogi_piece",
  7: "shogi_piece_sound",
  8: "shogi_effect",
  9: "shogi_ingame_bgm",
  10: "home_background",
  11: "character_present_currency",
  12: "ornament_sand_currency",
  13: "shogi_match_ticket_currency",
  14: "premium_pass_ticket",
  15: "coin_currency",
  16: "character_sand_currency",
  17: "character_ticket",
  18: "character_skin",
  19: "achievement_ticket",
};

// itemTypeを文字列に正規化する関数
const normalizeItemType = (itemType: string | number | undefined): string => {
  if (itemType === undefined || itemType === null) return "";
  if (typeof itemType === "number") {
    return ITEM_TYPE_ENUM_TO_STRING[itemType] ?? "";
  }
  return String(itemType);
};

// ========================================
// EnumDocsからItemType情報を取得
// ========================================
const itemTypeEnumInfo = enumDocumentationData.find(
  (e) => e.typeName === "ItemType"
);

// EnumDocsからItemType定数を動的に生成
// ITEM_TYPE_FREE_DIA -> free_dia のようにスネークケースに変換
const convertEnumNameToSnakeCase = (name: string): string => {
  // "ITEM_TYPE_FREE_DIA" -> "FREE_DIA" -> "free_dia"
  return name.replace("ITEM_TYPE_", "").toLowerCase();
};

// EnumDocsからITEM_TYPESを動的に生成
export const ITEM_TYPES: Record<string, string> = {};
if (itemTypeEnumInfo) {
  for (const value of itemTypeEnumInfo.values) {
    if (value.name === "ITEM_TYPE_NONE") continue;
    const key = value.name.replace("ITEM_TYPE_", "");
    const snakeValue = convertEnumNameToSnakeCase(value.name);
    ITEM_TYPES[key] = snakeValue;
  }
}

// EnumDocsからItemTypeの表示名を動的に生成
// コメントから "ItemTypeFreeDia 無償ダイヤ" -> "無償ダイヤ" を抽出
const extractLabelFromComment = (comment: string): string => {
  // "ItemTypeFreeDia 無償ダイヤ" のような形式から後半の日本語部分を抽出
  const parts = comment.split(" ");
  if (parts.length >= 2) {
    return parts.slice(1).join(" ");
  }
  return comment;
};

export const ITEM_TYPE_LABELS: Record<string, string> = {};
if (itemTypeEnumInfo) {
  for (const value of itemTypeEnumInfo.values) {
    if (value.name === "ITEM_TYPE_NONE") continue;
    const snakeValue = convertEnumNameToSnakeCase(value.name);
    ITEM_TYPE_LABELS[snakeValue] = extractLabelFromComment(value.comment);
  }
}

// OrnamentTypeのEnumからItemTypeを抽出
// ORNAMENT_TYPE_ORNAMENT_SHOGI_PIECE -> shogi_piece のように変換
const ornamentTypeEnumInfo = enumDocumentationData.find(
  (e) => e.typeName === "OrnamentType"
);

const convertOrnamentTypeToItemType = (name: string): string => {
  // "ORNAMENT_TYPE_ORNAMENT_SHOGI_PIECE" -> "SHOGI_PIECE" -> "shogi_piece"
  return name.replace("ORNAMENT_TYPE_ORNAMENT_", "").toLowerCase();
};

export const ORNAMENT_ITEM_TYPES: string[] = [];
if (ornamentTypeEnumInfo) {
  for (const value of ornamentTypeEnumInfo.values) {
    if (value.name === "ORNAMENT_TYPE_NONE") continue;
    const itemType = convertOrnamentTypeToItemType(value.name);
    ORNAMENT_ITEM_TYPES.push(itemType);
  }
}

// 通貨系のItemTypeは通貨マスタから取得するため、ここでは定義しない
// 代わりに、通貨かどうかの判定は通貨マスタのitem_typeを参照する

// ========================================
// UI用の報酬タイプ
// ========================================
interface CurrencyRewardUI {
  itemType: string;
  option: string; // MstCurrencyID
  acquiredAmount: number;
}

interface CharacterRewardUI {
  mstCharacterId: number;
}

interface OrnamentRewardUI {
  itemType: string;
  mstId: string;
}

interface AchievementRewardUI {
  mstAchievementId: number;
}

// RewardItemからUI用の報酬に分解
export const parseRewardItems = (items: RewardItem[] | undefined) => {
  const currencyRewards: CurrencyRewardUI[] = [];
  const characterRewards: CharacterRewardUI[] = [];
  const ornamentRewards: OrnamentRewardUI[] = [];
  const achievementRewards: AchievementRewardUI[] = [];

  if (!items)
    return {
      currencyRewards,
      characterRewards,
      ornamentRewards,
      achievementRewards,
    };

  for (const item of items) {
    // 数値（Protobuf enum）と文字列の両方に対応
    const rawItemType = (item as any).itemType ?? (item as any).item_type ?? "";
    const itemType = normalizeItemType(rawItemType);
    const category = (item as any).__category;

    if (
      itemType === ITEM_TYPES.CHARACTER_TICKET ||
      itemType === "character_ticket" ||
      category === "character"
    ) {
      const mstCharacterId = parseInt(item.option) || 0;
      characterRewards.push({ mstCharacterId });
    } else if (
      itemType === ITEM_TYPES.ACHIEVEMENT_TICKET ||
      itemType === "achievement_ticket" ||
      category === "achievement"
    ) {
      const mstAchievementId = parseInt(item.option) || 0;
      achievementRewards.push({ mstAchievementId });
    } else if (
      ORNAMENT_ITEM_TYPES.includes(itemType) ||
      category === "ornament"
    ) {
      // 装飾品系
      ornamentRewards.push({ itemType, mstId: item.option });
    } else {
      // 通貨系（装飾品・キャラクター・称号以外はすべて通貨として扱う）
      currencyRewards.push({
        itemType,
        option: item.option,
        acquiredAmount:
          (item as any).acquiredAmount ?? (item as any).acquired_amount ?? 0,
      });
    }
  }

  return {
    currencyRewards,
    characterRewards,
    ornamentRewards,
    achievementRewards,
  };
};

// UI用の報酬からRewardItemに変換
export const buildRewardItems = (
  currencyRewards: CurrencyRewardUI[],
  characterRewards: CharacterRewardUI[],
  ornamentRewards: OrnamentRewardUI[],
  achievementRewards: AchievementRewardUI[]
): RewardItem[] => {
  const result: RewardItem[] = [];

  for (const curr of currencyRewards) {
    result.push({
      itemType: curr.itemType,
      option: curr.option,
      acquiredAmount: curr.acquiredAmount,
      __category: "currency", // カテゴリ識別用
    } as RewardItem);
  }

  for (const char of characterRewards) {
    result.push({
      itemType: ITEM_TYPES.CHARACTER_TICKET,
      option: String(char.mstCharacterId),
      acquiredAmount: 1,
      __category: "character",
    } as RewardItem);
  }

  for (const orn of ornamentRewards) {
    result.push({
      itemType: orn.itemType,
      option: orn.mstId,
      acquiredAmount: 1,
      __category: "ornament",
    } as RewardItem);
  }

  for (const ach of achievementRewards) {
    result.push({
      itemType: "achievement_ticket",
      option: String(ach.mstAchievementId),
      acquiredAmount: 1,
      __category: "achievement",
    } as RewardItem);
  }

  return result;
};

// ========================================
// コンポーネント Props
// ========================================
export interface RewardEditorProps {
  value?: RewardItem[];
  onChange: (rewards: RewardItem[]) => void;
  className?: string;
  /** 表示するセクション（デフォルト: すべて表示） */
  sections?: ("currency" | "character" | "ornament" | "achievement")[];
  /** 読み取り専用モード */
  readOnly?: boolean;
}

// ========================================
// 通貨報酬フォームコンポーネント
// ========================================
interface CurrencyRewardFormProps {
  item: CurrencyRewardUI;
  index: number;
  onUpdate: (index: number, updated: CurrencyRewardUI) => void;
  onRemove: (index: number) => void;
  readOnly?: boolean;
}

const CurrencyRewardForm: React.FC<CurrencyRewardFormProps> = ({
  item,
  index,
  onUpdate,
  onRemove,
  readOnly = false,
}) => {
  const isPremiumPass = item.itemType === ITEM_TYPES.PREMIUM_PASS_TICKET;

  // 通貨選択時のハンドラー
  const handleCurrencyChange = (id: MstCurrencyID | undefined) => {
    if (id === undefined || id === 0) {
      onUpdate(index, {
        ...item,
        option: "",
      });
      return;
    }
    const currency = MasterDataClient.getMstCurrency(id);
    if (currency) {
      // 通貨マスタのitem_typeを直接使用
      onUpdate(index, {
        ...item,
        itemType: currency.item_type,
        option: String(currency.id),
      });
    }
  };

  // 通貨種別の表示名を取得（item_typeから直接ラベルを取得）
  const getItemTypeLabel = (itemType: string): string => {
    return ITEM_TYPE_LABELS[itemType] || itemType;
  };

  const currentValue = item.option
    ? (parseInt(item.option) as MstCurrencyID)
    : undefined;

  return (
    <div className="reward-editor-item">
      <div className="reward-editor-item-header">
        <span className="reward-editor-item-index">#{index + 1}</span>
        {!readOnly && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onRemove(index)}
          >
            ×
          </button>
        )}
      </div>
      <div className="reward-editor-item-content">
        <div className="reward-editor-field">
          <label className="reward-editor-label">
            通貨マスタ <span className="required">*</span>
          </label>
          <MstCurrencySelector
            value={currentValue}
            onChange={handleCurrencyChange}
            disabled={readOnly}
            allowEmpty={false}
            searchColumns={CURRENCY_SEARCH_COLUMNS}
            renderItemLabel={(currency) =>
              currency.name || `ID: ${currency.id}`
            }
            renderSelectedInfo={(currency) => (
              <>
                <span className="master-selector-dropdown__selected-id">
                  ID: {currency.id}
                </span>
                <span className="master-selector-dropdown__selected-label">
                  {currency.name}
                </span>
                <span className="master-selector-dropdown__selected-meta">
                  {getItemTypeLabel(currency.item_type)}
                </span>
              </>
            )}
          />
        </div>
        {isPremiumPass && (
          <div className="reward-editor-field">
            <p className="reward-editor-field-info">
              ※
              プレミアムパスチケットはOptionを使用しません。Amountが日数として扱われます。
            </p>
          </div>
        )}
        <div className="reward-editor-field">
          <label className="reward-editor-label">
            {isPremiumPass ? "日数" : "獲得数量"}{" "}
            <span className="required">*</span>
          </label>
          <input
            type="number"
            value={item.acquiredAmount || ""}
            onChange={(e) =>
              onUpdate(index, {
                ...item,
                acquiredAmount: Number(e.target.value) || 0,
              })
            }
            className="reward-editor-input form-input"
            placeholder={isPremiumPass ? "日数を入力" : "数量を入力"}
            min="0"
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

// ========================================
// 装飾品報酬フォームコンポーネント
// ========================================
interface OrnamentRewardFormProps {
  item: OrnamentRewardUI;
  index: number;
  onUpdate: (index: number, updated: OrnamentRewardUI) => void;
  onRemove: (index: number) => void;
  readOnly?: boolean;
}

const OrnamentRewardForm: React.FC<OrnamentRewardFormProps> = ({
  item,
  index,
  onUpdate,
  onRemove,
  readOnly = false,
}) => {
  const ornamentTypeOptions = useMemo(() => {
    return ORNAMENT_ITEM_TYPES.map((type) => ({
      value: type,
      label: ITEM_TYPE_LABELS[type] || type,
    }));
  }, []);

  // ItemTypeからマスタセレクターを取得するマッピング
  const ITEM_TYPE_TO_MASTER_SELECTOR: Record<string, string> = {
    icon: "master.MstIconID",
    icon_frame: "master.MstIconFrameID",
    shogi_board: "master.MstShogiBoardID",
    shogi_piece: "master.MstShogiPieceID",
    shogi_piece_sound: "master.MstShogiPieceSoundID",
    shogi_effect: "master.MstShogiPieceEffectID",
    shogi_ingame_b_g_m: "master.MstShogiIngameBGMID",
    home_background: "master.MstHomeBackgroundID",
  };

  const getOrnamentMasterSelector = (itemType: string) => {
    const selectorType = ITEM_TYPE_TO_MASTER_SELECTOR[itemType];
    if (selectorType) {
      return MasterDataSelectors.getSelectorComponentByType(selectorType);
    }
    return null;
  };

  const OrnamentMasterSelector = getOrnamentMasterSelector(item.itemType);

  return (
    <div className="reward-editor-item">
      <div className="reward-editor-item-header">
        <span className="reward-editor-item-index">#{index + 1}</span>
        {!readOnly && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onRemove(index)}
          >
            ×
          </button>
        )}
      </div>
      <div className="reward-editor-item-content">
        <div className="reward-editor-field">
          <label className="reward-editor-label">
            装飾品種別 <span className="required">*</span>
          </label>
          <select
            value={item.itemType}
            onChange={(e) =>
              onUpdate(index, { ...item, itemType: e.target.value, mstId: "" })
            }
            className="reward-editor-input form-input"
            disabled={readOnly}
          >
            <option value="">選択してください</option>
            {ornamentTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="reward-editor-field">
          <label className="reward-editor-label">
            {ITEM_TYPE_LABELS[item.itemType] || "装飾品"}ID{" "}
            <span className="required">*</span>
          </label>
          {OrnamentMasterSelector ? (
            <OrnamentMasterSelector
              value={item.mstId ? Number(item.mstId) : 0}
              onChange={(newValue: number) =>
                onUpdate(index, { ...item, mstId: String(newValue) })
              }
              placeholder={`${
                ITEM_TYPE_LABELS[item.itemType] || "装飾品"
              }を選択`}
              allowEmpty={true}
              className="reward-editor-input master-selector--compact"
              disabled={readOnly}
            />
          ) : item.itemType === "" ? (
            <div className="reward-editor-input-placeholder">
              先に装飾品種別を選択してください
            </div>
          ) : (
            <input
              type="text"
              value={item.mstId || ""}
              onChange={(e) =>
                onUpdate(index, { ...item, mstId: e.target.value })
              }
              className="reward-editor-input form-input"
              placeholder="装飾品IDを入力"
              disabled={readOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================
export const RewardEditor: React.FC<RewardEditorProps> = ({
  value,
  onChange,
  className = "",
  sections = ["currency", "character", "ornament", "achievement"],
  readOnly = false,
}) => {
  // valueの参照を追跡して、外部からの変更かどうかを判断
  const lastValueRef = React.useRef<RewardItem[] | undefined>(undefined);
  const isInternalChange = React.useRef(false);

  const parsed = useMemo(() => parseRewardItems(value), [value]);

  const [currencyRewards, setCurrencyRewards] = useState<CurrencyRewardUI[]>(
    parsed.currencyRewards
  );
  const [characters, setCharacters] = useState<CharacterRewardUI[]>(
    parsed.characterRewards
  );
  const [ornaments, setOrnaments] = useState<OrnamentRewardUI[]>(
    parsed.ornamentRewards
  );
  const [achievements, setAchievements] = useState<AchievementRewardUI[]>(
    parsed.achievementRewards
  );

  // 外部からvalueが変更された場合のみ状態を更新
  useEffect(() => {
    // 内部変更の場合はスキップ
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    // valueが実際に変更された場合のみ更新
    if (value !== lastValueRef.current) {
      const newParsed = parseRewardItems(value);
      setCurrencyRewards(newParsed.currencyRewards);
      setCharacters(newParsed.characterRewards);
      setOrnaments(newParsed.ornamentRewards);
      setAchievements(newParsed.achievementRewards);
      lastValueRef.current = value;
    }
  }, [value]);

  // onChangeをラップして内部変更フラグを設定
  const handleChange = React.useCallback(
    (newRewards: RewardItem[]) => {
      isInternalChange.current = true;
      lastValueRef.current = newRewards;
      onChange(newRewards);
    },
    [onChange]
  );

  // 通貨操作
  const addCurrencyReward = () => {
    const newItem: CurrencyRewardUI = {
      itemType: "",
      option: "",
      acquiredAmount: 0,
    };
    const newItems = [...currencyRewards, newItem];
    setCurrencyRewards(newItems);
    handleChange(
      buildRewardItems(newItems, characters, ornaments, achievements)
    );
  };

  const removeCurrencyReward = (index: number) => {
    const newItems = currencyRewards.filter((_, i) => i !== index);
    setCurrencyRewards(newItems);
    handleChange(
      buildRewardItems(newItems, characters, ornaments, achievements)
    );
  };

  const updateCurrencyReward = (index: number, updated: CurrencyRewardUI) => {
    const newItems = currencyRewards.map((item, i) =>
      i === index ? updated : item
    );
    setCurrencyRewards(newItems);
    handleChange(
      buildRewardItems(newItems, characters, ornaments, achievements)
    );
  };

  // キャラクター操作
  const addCharacterReward = () => {
    const newChar: CharacterRewardUI = { mstCharacterId: 0 };
    const newChars = [...characters, newChar];
    setCharacters(newChars);
    handleChange(
      buildRewardItems(currencyRewards, newChars, ornaments, achievements)
    );
  };

  const removeCharacterReward = (index: number) => {
    const newChars = characters.filter((_, i) => i !== index);
    setCharacters(newChars);
    handleChange(
      buildRewardItems(currencyRewards, newChars, ornaments, achievements)
    );
  };

  const updateCharacterReward = (index: number, updated: CharacterRewardUI) => {
    const newChars = characters.map((char, i) =>
      i === index ? updated : char
    );
    setCharacters(newChars);
    handleChange(
      buildRewardItems(currencyRewards, newChars, ornaments, achievements)
    );
  };

  // 装飾品操作
  const addOrnamentReward = () => {
    const newOrn: OrnamentRewardUI = { itemType: "", mstId: "" };
    const newOrns = [...ornaments, newOrn];
    setOrnaments(newOrns);
    handleChange(
      buildRewardItems(currencyRewards, characters, newOrns, achievements)
    );
  };

  const removeOrnamentReward = (index: number) => {
    const newOrns = ornaments.filter((_, i) => i !== index);
    setOrnaments(newOrns);
    handleChange(
      buildRewardItems(currencyRewards, characters, newOrns, achievements)
    );
  };

  const updateOrnamentReward = (index: number, updated: OrnamentRewardUI) => {
    const newOrns = ornaments.map((orn, i) => (i === index ? updated : orn));
    setOrnaments(newOrns);
    handleChange(
      buildRewardItems(currencyRewards, characters, newOrns, achievements)
    );
  };

  // 称号操作
  const addAchievementReward = () => {
    const newAch: AchievementRewardUI = { mstAchievementId: 0 };
    const newAchs = [...achievements, newAch];
    setAchievements(newAchs);
    handleChange(
      buildRewardItems(currencyRewards, characters, ornaments, newAchs)
    );
  };

  const removeAchievementReward = (index: number) => {
    const newAchs = achievements.filter((_, i) => i !== index);
    setAchievements(newAchs);
    handleChange(
      buildRewardItems(currencyRewards, characters, ornaments, newAchs)
    );
  };

  const updateAchievementReward = (
    index: number,
    updated: AchievementRewardUI
  ) => {
    const newAchs = achievements.map((ach, i) => (i === index ? updated : ach));
    setAchievements(newAchs);
    handleChange(
      buildRewardItems(currencyRewards, characters, ornaments, newAchs)
    );
  };

  const MstCharacterSelector = MasterDataSelectors.getSelectorComponentByType(
    "master.MstCharacterID"
  );
  const MstAchievementSelector = MasterDataSelectors.getSelectorComponentByType(
    "master.MstAchievementID"
  );

  const showCurrency = sections.includes("currency");
  const showCharacter = sections.includes("character");
  const showOrnament = sections.includes("ornament");
  const showAchievement = sections.includes("achievement");

  return (
    <div className={`reward-editor ${className}`}>
      {/* 通貨報酬 */}
      {showCurrency && (
        <div className="reward-editor-section">
          <div className="reward-editor-section-header">
            <h3>通貨報酬</h3>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addCurrencyReward}
              >
                + 追加
              </button>
            )}
          </div>
          <div className="reward-editor-items">
            {currencyRewards
              .filter((item) => item != null)
              .map((item, index) => (
                <CurrencyRewardForm
                  key={index}
                  item={item}
                  index={index}
                  onUpdate={updateCurrencyReward}
                  onRemove={removeCurrencyReward}
                  readOnly={readOnly}
                />
              ))}
            {currencyRewards.length === 0 && (
              <div className="reward-editor-empty">通貨報酬はありません</div>
            )}
          </div>
        </div>
      )}

      {/* キャラクター報酬 */}
      {showCharacter && (
        <div className="reward-editor-section">
          <div className="reward-editor-section-header">
            <h3>キャラクター報酬</h3>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addCharacterReward}
              >
                + 追加
              </button>
            )}
          </div>
          <div className="reward-editor-items">
            {characters
              .filter((char) => char != null)
              .map((character, index) => (
                <div key={index} className="reward-editor-item">
                  <div className="reward-editor-item-header">
                    <span className="reward-editor-item-index">
                      #{index + 1}
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeCharacterReward(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="reward-editor-item-content">
                    <div className="reward-editor-field">
                      <label className="reward-editor-label">
                        キャラクターID <span className="required">*</span>
                      </label>
                      {MstCharacterSelector ? (
                        <MstCharacterSelector
                          value={character.mstCharacterId}
                          onChange={(newValue: number) =>
                            updateCharacterReward(index, {
                              mstCharacterId: newValue,
                            })
                          }
                          placeholder="キャラクターを選択"
                          allowEmpty={true}
                          className="reward-editor-input master-selector--compact"
                          disabled={readOnly}
                        />
                      ) : (
                        <input
                          type="number"
                          value={character.mstCharacterId || ""}
                          onChange={(e) =>
                            updateCharacterReward(index, {
                              mstCharacterId: Number(e.target.value) || 0,
                            })
                          }
                          className="reward-editor-input form-input"
                          placeholder="キャラクターIDを入力"
                          disabled={readOnly}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            {characters.length === 0 && (
              <div className="reward-editor-empty">
                キャラクター報酬はありません
              </div>
            )}
          </div>
        </div>
      )}

      {/* 装飾品報酬 */}
      {showOrnament && (
        <div className="reward-editor-section">
          <div className="reward-editor-section-header">
            <h3>装飾品報酬</h3>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addOrnamentReward}
              >
                + 追加
              </button>
            )}
          </div>
          <div className="reward-editor-items">
            {ornaments.map((ornament, index) => (
              <OrnamentRewardForm
                key={index}
                item={ornament}
                index={index}
                onUpdate={updateOrnamentReward}
                onRemove={removeOrnamentReward}
                readOnly={readOnly}
              />
            ))}
            {ornaments.length === 0 && (
              <div className="reward-editor-empty">装飾品報酬はありません</div>
            )}
          </div>
        </div>
      )}

      {/* 称号報酬 */}
      {showAchievement && (
        <div className="reward-editor-section">
          <div className="reward-editor-section-header">
            <h3>称号報酬</h3>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addAchievementReward}
              >
                + 追加
              </button>
            )}
          </div>
          <div className="reward-editor-items">
            {achievements.map((achievement, index) => (
              <div key={index} className="reward-editor-item">
                <div className="reward-editor-item-header">
                  <span className="reward-editor-item-index">#{index + 1}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeAchievementReward(index)}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="reward-editor-item-content">
                  <div className="reward-editor-field">
                    <label className="reward-editor-label">
                      称号ID <span className="required">*</span>
                    </label>
                    {MstAchievementSelector ? (
                      <MstAchievementSelector
                        value={achievement.mstAchievementId}
                        onChange={(newValue: number) =>
                          updateAchievementReward(index, {
                            mstAchievementId: newValue,
                          })
                        }
                        placeholder="称号を選択"
                        allowEmpty={true}
                        className="reward-editor-input master-selector--compact"
                        disabled={readOnly}
                      />
                    ) : (
                      <input
                        type="number"
                        value={achievement.mstAchievementId || ""}
                        onChange={(e) =>
                          updateAchievementReward(index, {
                            mstAchievementId: Number(e.target.value) || 0,
                          })
                        }
                        className="reward-editor-input form-input"
                        placeholder="称号IDを入力"
                        disabled={readOnly}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {achievements.length === 0 && (
              <div className="reward-editor-empty">称号報酬はありません</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardEditor;
